import { seedRandom, sampleRowCount, linkSelfRelationField, crossRelationValue, projectionOf, isRelationPlaceholder, RESERVED_FIELD_NAMES, sampleRow, randomPastDate, randomInt } from '@almadar/core/mock';
import { createLogger } from '@almadar/logger';

// src/mock/InMemoryPersistence.ts
var InMemoryPersistence = class {
  data = /* @__PURE__ */ new Map();
  idCounter = 0;
  /**
   * Seed the store with pre-existing rows.
   *
   * Accepts either a plain `Record<entityType, EntityRow[]>` or an iterable
   * of `[entityType, EntityRow[]]` entries. Rows without an `id` get one
   * generated at insert time; rows with an `id` keep it (so re-seeding
   * after a schema rebuild preserves identities used in render bindings).
   */
  seed(seedData) {
    const entries = Symbol.iterator in Object(seedData) ? seedData : Object.entries(seedData);
    for (const [entityType, rows] of entries) {
      if (!this.data.has(entityType)) {
        this.data.set(entityType, /* @__PURE__ */ new Map());
      }
      const collection = this.data.get(entityType);
      for (const row of rows) {
        const id = row.id || `${entityType}-${++this.idCounter}`;
        collection.set(id, { ...row, id });
      }
    }
  }
  async create(entityType, data) {
    const id = data.id || `${entityType}-${++this.idCounter}`;
    if (!this.data.has(entityType)) {
      this.data.set(entityType, /* @__PURE__ */ new Map());
    }
    this.data.get(entityType).set(id, { ...data, id });
    return { id };
  }
  async update(entityType, id, data) {
    const collection = this.data.get(entityType);
    if (collection?.has(id)) {
      const existing = collection.get(id);
      collection.set(id, { ...existing, ...data });
    }
  }
  async delete(entityType, id) {
    this.data.get(entityType)?.delete(id);
  }
  async getById(entityType, id) {
    return this.data.get(entityType)?.get(id) || null;
  }
  async list(entityType) {
    const collection = this.data.get(entityType);
    return collection ? Array.from(collection.values()) : [];
  }
  /**
   * Snapshot the entire store as a plain object (entityType → rows).
   * Useful for feeding a fresh render-time binding layer with the
   * current persistence view.
   */
  snapshot() {
    const out = {};
    for (const [entityType, collection] of this.data) {
      out[entityType] = Array.from(collection.values());
    }
    return out;
  }
};
var mockLog = createLogger("almadar:runtime:mock");
var DEFAULT_MOCK_SEED = 42;
var SEED_REFERENCE_TIMESTAMP = "2024-01-01T00:00:00.000Z";
var MS_PER_DAY = 864e5;
function isoTimestamp(value) {
  if (value instanceof Date) return value.toISOString();
  return typeof value === "string" && value.length > 0 ? value : void 0;
}
function stampsRow(column, index) {
  return column === "id" ? index === 0 : index % 2 === 0;
}
function isRelationField(field) {
  return field.type === "relation";
}
function stampedCellKey(entity, id, column) {
  return `${entity}\0${id}\0${column}`;
}
function entitySchemaOf(entity) {
  return {
    name: entity.name,
    id: entity.id,
    collection: entity.collection,
    fields: entity.fields.filter((f) => typeof f.name === "string" && f.name.length > 0),
    seedData: entity.instances,
    identity: entity.identity,
    persistence: entity.persistence
  };
}
var MockPersistenceAdapter = class _MockPersistenceAdapter {
  stores = /* @__PURE__ */ new Map();
  schemas = /* @__PURE__ */ new Map();
  idCounters = /* @__PURE__ */ new Map();
  /** entityId -> store key, so relation lookups can prefer the id sibling over `relation.entity` name-matching. */
  storeNameById = /* @__PURE__ */ new Map();
  /** normalized entity name -> store key (the declared collection, else the name).
   *  Entities sharing a `persistent:` collection resolve to the same store. */
  storeKeyByEntity = /* @__PURE__ */ new Map();
  /** store key -> the first registrant's entity name, used as the minted-id label
   *  so rows in a shared collection carry one consistent id family. */
  idLabelByStoreKey = /* @__PURE__ */ new Map();
  config;
  /**
   * Every (entity, row id, column) cell `seed()` stamped with `config.ownerId`.
   * Seeding is eager and runs once at registration, before a dev host's
   * viewer is known to have changed — see `restampOwner`, which walks this
   * list to re-point the stamp when the default user changes later.
   */
  ownerStampedCells = [];
  /**
   * Owner cells the gate kept from the viewer at seed time with no eligible fallback
   * owner. A later persona switch stamps the ones the new viewer may create, so a host
   * that seeds as the synthetic viewer and then presents a roster persona gives that
   * persona its rows.
   */
  ownerDeferredCells = [];
  constructor(config = {}) {
    this.config = {
      defaultSeedCount: 6,
      debug: false,
      ...config,
      // Apply default after spread so an undefined `seed` in the
      // input doesn't overwrite the default.
      seed: config.seed ?? DEFAULT_MOCK_SEED
    };
    seedRandom(this.config.seed);
    mockLog.debug("mock:adapter:init", { seed: this.config.seed });
  }
  /**
   * Add owner columns discovered after construction.
   *
   * The adapter is built before any schema is registered, so schema-DERIVED
   * owner columns (relation fields pointing at the `[identity]` entity) can
   * only arrive later.
   *
   * ⚠️ Seeding is EAGER — `registerEntity()` seeds immediately — so callers must
   * supply these columns BEFORE registering any orbital. Calling this afterwards
   * silently stamps nothing.
   */
  addOwnerFields(fields) {
    if (fields.length === 0) return;
    const merged = /* @__PURE__ */ new Set([...this.config.ownerFields ?? [], ...fields]);
    this.config.ownerFields = [...merged];
  }
  /**
   * Re-point every owner-stamped cell from the current `ownerId` to
   * `newOwnerId`, and remember `newOwnerId` for any future seed.
   *
   * Seeding is eager (`registerEntity()` seeds immediately, at construction
   * time), so the columns above are frozen to whichever id was the default
   * user THEN — a dev host switching viewers later (`setDefaultUser` /
   * `POST /persona`) left the stamp pointing at the old id forever, so an
   * ownership-scoped view for the NEW viewer stayed empty. This does not
   * re-seed (eager seeding is intentional, see the class doc); it only
   * re-labels the cells `seed()` already marked as viewer-owned.
   *
   * No-op when there is no new id, or it matches the current one — an
   * anonymous switch (`newOwnerId === undefined`) leaves existing stamps as
   * they are rather than clearing a non-nullable owner column.
   *
   * Both this re-labeling AND `seed()`'s original stamping consult the
   * `ownerGate` hook (see `setOwnerGate`) — a re-register reseeds identical
   * rows, so gating only the restamp path leaks the moment a client
   * connects and the deterministic reseed stamps with the updated ownerId.
   */
  restampOwner(newOwnerId) {
    const oldOwnerId = this.config.ownerId;
    this.config.ownerId = newOwnerId;
    if (!newOwnerId || newOwnerId === oldOwnerId) return;
    let skipped = 0;
    let fallbackStamps = 0;
    for (const cell of this.ownerStampedCells) {
      const row = this.stores.get(cell.entity)?.get(cell.id);
      if (!row) continue;
      if (cell.column === "id") {
        if (this.ownerGate && !this.ownerGate(cell.entity, { ...row, id: newOwnerId })) {
          skipped++;
          continue;
        }
        this.rekeyRow(cell.entity, cell.id, newOwnerId);
        continue;
      }
      if (!this.viewerMayOwn(cell.entity, cell.column, row, newOwnerId)) {
        const fallbackId = this.firstEligibleOwner(cell.entity, cell.column, row);
        if (fallbackId === void 0) {
          skipped++;
          continue;
        }
        row[cell.column] = fallbackId;
        fallbackStamps++;
        continue;
      }
      row[cell.column] = newOwnerId;
    }
    const stillDeferred = [];
    for (const cell of this.ownerDeferredCells) {
      const row = this.stores.get(cell.entity)?.get(cell.id);
      if (!row) continue;
      if (!this.viewerMayOwn(cell.entity, cell.column, row, newOwnerId)) {
        stillDeferred.push(cell);
        continue;
      }
      if (cell.column === "id") this.rekeyRow(cell.entity, cell.id, newOwnerId);
      else row[cell.column] = newOwnerId;
      this.ownerStampedCells.push({ ...cell, id: cell.column === "id" ? newOwnerId : cell.id });
    }
    this.ownerDeferredCells = stillDeferred;
    mockLog.debug("mock:owner-restamped", {
      from: oldOwnerId,
      to: newOwnerId,
      cells: this.ownerStampedCells.length,
      skipped,
      fallbackStamps
    });
  }
  /**
   * Give a row a new id in place: the store keeps its order and every stamped
   * cell that named the old id follows it.
   */
  rekeyRow(storeKey, oldId, newId) {
    const store = this.stores.get(storeKey);
    const row = store?.get(oldId);
    if (!store || !row || oldId === newId) return;
    const entries = [...store.entries()].map(([id, r]) => id === oldId ? [newId, { ...r, id: newId }] : [id, r]);
    store.clear();
    for (const [id, r] of entries) store.set(id, r);
    for (const cell of [...this.ownerStampedCells, ...this.ownerDeferredCells]) {
      if (cell.entity === storeKey && cell.id === oldId) cell.id = newId;
    }
  }
  /**
   * The host's answer to "may the current viewer OWN this row?" — evaluated
   * against the candidate row with the owner column already re-pointed,
   * `storeKey` being the collection key the stores are keyed by. The runtime
   * installs the registered schema's `@create` directive here (the single
   * authority on who authors; no role heuristics), so a read-only persona
   * never inherits authorship of drafts from the demo stamper — neither on a
   * persona switch (`restampOwner`) nor on a deterministic reseed (`seed`).
   * Absent hook = stamp unconditionally (pre-role-model behavior).
   */
  ownerGate;
  setOwnerGate(gate) {
    this.ownerGate = gate;
  }
  /**
   * The host's answer to "may THIS identity row own this candidate row?" —
   * evaluated per candidate rather than against the fixed default viewer, so
   * `linkRelationFields` and the eligible-owner fallback (see
   * `firstEligibleOwner`) can pick a policy-valid owner from the whole
   * identity roster instead of only ever checking the current default user.
   * Absent hook = no candidate filtering (pre-role-model behavior).
   */
  ownerCandidateGate;
  setOwnerCandidateGate(gate) {
    this.ownerCandidateGate = gate;
  }
  /**
   * "Does `@read` admit this identity to `candidateRow`?" When some roster
   * identity is admitted only while the owner column holds its id, the column
   * is ownership-scoped (`Employee.staffAccount`: the employee the row is
   * about) and every identity `@read` admits as its owner may own it — so each
   * such persona gets rows of its own. Otherwise `@create` decides as before.
   */
  ownerReadGate;
  setOwnerReadGate(gate) {
    this.ownerReadGate = gate;
  }
  /** The identities (seed order) that may own an ownership-scoped `column`; undefined when `@read` does not scope it. */
  readOwners(storeKey, column, row) {
    const gate = this.ownerReadGate;
    if (!gate || column === "id") return void 0;
    const identityStoreKey = this.resolveOwnerColumnTargetStore(storeKey, column);
    const identityStore = identityStoreKey ? this.stores.get(identityStoreKey) : void 0;
    if (!identityStore) return void 0;
    const identities = [...identityStore.values()];
    const admitsAsOwner = (identityRow) => gate(storeKey, { ...row, [column]: identityRow["id"] }, identityRow);
    const scoped = identities.some((identityRow) => admitsAsOwner(identityRow) && !gate(storeKey, { ...row, [column]: "" }, identityRow));
    if (!scoped) return void 0;
    return identities.filter(admitsAsOwner).map((identityRow) => identityRow["id"]);
  }
  /** May the viewer own `row`'s `column` — `@read` ownership when it scopes the column, else the `@create` gate. */
  viewerMayOwn(storeKey, column, row, viewerId) {
    const readers = this.readOwners(storeKey, column, row);
    if (readers !== void 0) return readers.includes(viewerId);
    return !this.ownerGate || this.ownerGate(storeKey, { ...row, [column]: viewerId });
  }
  /**
   * The store an owner column's relation field targets, resolved the same
   * way `linkRelationFields` resolves any relation target (id sibling wins
   * over the `entity` name string) — but keyed by STORE, mirroring that
   * method's first-declarer-per-collection rule, so `seed()` and
   * `restampOwner()` can find it before a `linkRelationFields` pass has run
   * this registration. Undefined when no registered schema on `storeKey`
   * declares `column` as a relation field.
   */
  resolveOwnerColumnTargetStore(storeKey, column) {
    for (const [normalizedName, schema] of this.schemas) {
      if ((this.storeKeyByEntity.get(normalizedName) ?? normalizedName) !== storeKey) continue;
      const field = schema.fields.find((f) => f.name === column);
      if (field && isRelationField(field)) {
        return (field.relation.entityId && this.storeNameById.get(field.relation.entityId)) ?? this.resolveStoreKey(field.relation.entity);
      }
    }
    return void 0;
  }
  /**
   * The first identity-store row (seed order) whose persona is eligible, per
   * the installed `ownerCandidateGate`, to own `row`'s `column` in `storeKey`
   * — the deterministic replacement for "skip the stamp" when the CURRENT
   * viewer fails the `@create` policy (e.g. a reader-default persona against
   * an author|reviewer-only policy). Undefined when no gate is installed, the
   * column isn't a resolvable relation, or no identity row is eligible — the
   * caller keeps today's skip behavior in that case.
   */
  firstEligibleOwner(storeKey, column, row) {
    const readers = this.readOwners(storeKey, column, row);
    if (readers !== void 0) return readers[0];
    if (!this.ownerCandidateGate) return void 0;
    const identityStoreKey = this.resolveOwnerColumnTargetStore(storeKey, column);
    const identityStore = identityStoreKey ? this.stores.get(identityStoreKey) : void 0;
    if (!identityStore) return void 0;
    for (const identityRow of identityStore.values()) {
      const candidateId = identityRow["id"];
      if (this.ownerCandidateGate(storeKey, { ...row, [column]: candidateId }, identityRow)) {
        return candidateId;
      }
    }
    return void 0;
  }
  /** Re-anchor the PRNG to the configured seed. Called before every
   *  re-seed loop so identical reseed sequences produce identical rows
   *  (timestamps + generated fields). Without this, the first
   *  reseed produces row set A, the second produces row set B, and
   *  diff observers see all rows as "changed" between frames. */
  resetFakerSeed() {
    if (this.config.seed !== void 0) {
      seedRandom(this.config.seed);
    }
  }
  // ============================================================================
  // Store Management
  // ============================================================================
  /** Resolve an entity name to its store key: the declared collection when the
   *  entity is registered, the lowercased name otherwise (unregistered ad-hoc
   *  creates keep working). */
  resolveStoreKey(entityName) {
    const normalized = entityName.toLowerCase();
    return this.storeKeyByEntity.get(normalized) ?? normalized;
  }
  getStore(entityName) {
    const key = this.resolveStoreKey(entityName);
    if (!this.stores.has(key)) {
      this.stores.set(key, /* @__PURE__ */ new Map());
      this.idCounters.set(key, 0);
    }
    return this.stores.get(key);
  }
  nextId(entityName) {
    const key = this.resolveStoreKey(entityName);
    const counter = (this.idCounters.get(key) ?? 0) + 1;
    this.idCounters.set(key, counter);
    const label = this.idLabelByStoreKey.get(key) ?? entityName;
    return `${this.capitalizeFirst(label)} Id ${counter}`;
  }
  // ============================================================================
  // Schema & Seeding
  // ============================================================================
  /**
   * Register an entity schema and seed mock data.
   * If the schema has seedData, those instances are used directly.
   * Otherwise, random mock data is generated with the seeded PRNG.
   */
  registerEntity(schema, seedCount) {
    const normalized = schema.name.toLowerCase();
    const storeKey = (schema.collection ?? schema.name).toLowerCase();
    this.storeKeyByEntity.set(normalized, storeKey);
    if (!this.idLabelByStoreKey.has(storeKey)) {
      this.idLabelByStoreKey.set(storeKey, schema.name);
    }
    this.schemas.set(normalized, schema);
    if (schema.id) {
      this.storeNameById.set(schema.id, storeKey);
    }
    const alreadySeeded = (this.stores.get(storeKey)?.size ?? 0) > 0;
    if (schema.seedData && schema.seedData.length > 0) {
      this.seedFromInstances(schema.name, schema.seedData);
    } else if (alreadySeeded) {
      this.backfillFields(storeKey, schema);
    } else {
      const requested = seedCount ?? this.config.defaultSeedCount ?? 6;
      const count = sampleRowCount(
        { name: schema.name, persistence: schema.persistence, fields: schema.fields },
        requested
      );
      this.seed(schema.name, schema.fields, count, schema.persistence, schema.identity);
    }
    this.linkRelationFields();
    this.linkProjectedFields();
  }
  /**
   * Walk every row of every registered entity and fill in `type: "relation"`
   * fields with real IDs from the target entity's store. Without this pass,
   * relation fields stay as placeholder `[]` / `""` and `populateRelations`
   * in OrbitalServerRuntime has nothing to hydrate — catalog/preview demos
   * of nested-tree atoms (e.g. std-thread-comments-linear with ThreadPost.
   * replies → [ThreadPost]) render empty reply cards.
   *
   * Cross-entity relations take `crossRelationValue` from `@almadar/core/mock`:
   * deterministic (row *i* → target *i* mod n, or 2–3 consecutive ids), so no
   * PRNG draw happens here and every path links identically. Every
   * self-referential relation goes through `linkSelfRelationField`'s forest:
   * row 0 stays a root, row *i* parents to row ⌊(i−1)/2⌋ (scalar parent id for
   * `one`/`many-to-one`, the children-id list otherwise) — no cycles, and the
   * root stays deletable under `onDelete: restrict`. Only placeholder cells are
   * filled. Twin of `orbital-core/src/runtime/seed.rs` `link_relation_fields`.
   *
   * Entities sharing a collection are linked once per STORE, over the union
   * of their declared relation fields (first declarer of a field name wins).
   */
  linkRelationFields() {
    const relationFieldsByStore = /* @__PURE__ */ new Map();
    for (const [normalizedName, schema] of this.schemas) {
      const storeKey = this.storeKeyByEntity.get(normalizedName) ?? normalizedName;
      let fieldMap = relationFieldsByStore.get(storeKey);
      if (!fieldMap) {
        fieldMap = /* @__PURE__ */ new Map();
        relationFieldsByStore.set(storeKey, fieldMap);
      }
      for (const f of schema.fields) {
        if (isRelationField(f) && !fieldMap.has(f.name)) {
          fieldMap.set(f.name, f);
        }
      }
    }
    const ownerColumnsByStore = /* @__PURE__ */ new Map();
    for (const { entity, field } of this.parsedOwnerFields()) {
      const storeKey = this.resolveStoreKey(entity);
      const set = ownerColumnsByStore.get(storeKey) ?? /* @__PURE__ */ new Set();
      set.add(field);
      ownerColumnsByStore.set(storeKey, set);
    }
    const stampedCellKeys = new Set(
      this.ownerStampedCells.map((cell) => stampedCellKey(cell.entity, cell.id, cell.column))
    );
    for (const [storeKey, fieldMap] of relationFieldsByStore) {
      const store = this.stores.get(storeKey);
      if (!store || fieldMap.size === 0) continue;
      const rows = [...store.values()];
      const ownerColumns = ownerColumnsByStore.get(storeKey);
      for (const field of fieldMap.values()) {
        const isOwnerColumn = ownerColumns?.has(field.name) ?? false;
        const targetKey = (field.relation.entityId && this.storeNameById.get(field.relation.entityId)) ?? this.resolveStoreKey(field.relation.entity);
        const targetStore = this.stores.get(targetKey);
        if (!targetStore || targetStore.size === 0) continue;
        const cardinality = field.relation.cardinality ?? "many";
        const fillable = (row) => isRelationPlaceholder(row[field.name]) && !stampedCellKeys.has(stampedCellKey(storeKey, row["id"], field.name));
        if (targetStore === store) {
          if (isOwnerColumn) {
            for (const row of rows) {
              if (fillable(row)) row[field.name] = row["id"];
            }
            continue;
          }
          linkSelfRelationField(rows, { name: field.name, cardinality }, (row) => !fillable(row));
          continue;
        }
        rows.forEach((row, i) => {
          if (!fillable(row)) return;
          let eligible = [...targetStore.keys()];
          const readers = isOwnerColumn ? this.readOwners(storeKey, field.name, row) : void 0;
          if (readers !== void 0) {
            eligible = readers;
          } else if (isOwnerColumn && this.ownerCandidateGate) {
            const gate = this.ownerCandidateGate;
            eligible = eligible.filter(
              (id) => gate(storeKey, { ...row, [field.name]: id }, targetStore.get(id))
            );
          }
          const value = crossRelationValue(eligible, i, cardinality);
          if (value !== void 0) row[field.name] = value;
        });
      }
    }
  }
  /**
   * Fill each `T.f` placeholder (`projectedFrom`) from `T`'s seeded rows: row *i* takes row
   * *i* mod n's `f` (wrapped in an array for an array field). Only placeholder cells are
   * filled, as for relations, so relinking after every registration never rewrites data.
   * Twin of `orbital-core/src/runtime/seed.rs` `link_projections`.
   */
  linkProjectedFields() {
    const stampedCellKeys = new Set(
      this.ownerStampedCells.map((cell) => stampedCellKey(cell.entity, cell.id, cell.column))
    );
    const done = /* @__PURE__ */ new Set();
    for (const [normalizedName, schema] of this.schemas) {
      const storeKey = this.storeKeyByEntity.get(normalizedName) ?? normalizedName;
      const store = this.stores.get(storeKey);
      if (!store) continue;
      for (const field of schema.fields) {
        const projection = projectionOf(field);
        if (projection === void 0 || done.has(`${storeKey}\0${field.name}`)) continue;
        done.add(`${storeKey}\0${field.name}`);
        const target = this.stores.get(this.resolveStoreKey(projection.type));
        if (!target || target.size === 0) continue;
        const targetRows = [...target.values()];
        [...store.values()].forEach((row, i) => {
          if (!isRelationPlaceholder(row[field.name])) return;
          if (stampedCellKeys.has(stampedCellKey(storeKey, row["id"], field.name))) return;
          const picked = targetRows[i % targetRows.length][projection.field] ?? null;
          row[field.name] = field.type === "array" ? [picked] : picked;
        });
      }
    }
  }
  /**
   * Fill fields a late-registering sibling schema declares that the shared
   * store's existing rows lack. The first registrant on a collection seeds
   * with ITS field list; a sibling declaring extra columns (e.g. `Tag.parentId`
   * arriving after `WikiTagRef {id,name}`) would otherwise read `undefined`
   * where its filters expect a value. Values come from the same canonical
   * sample policy as seeding; relation fields are then linked by the global
   * `linkRelationFields` pass that follows registration.
   */
  backfillFields(storeKey, schema) {
    const store = this.stores.get(storeKey);
    if (!store) return;
    const candidates = schema.fields.filter(
      (f) => f.name === void 0 || !RESERVED_FIELD_NAMES.has(f.name)
    );
    if (candidates.length === 0) return;
    let index = 0;
    for (const row of store.values()) {
      index += 1;
      const absent = candidates.filter((f) => row[f.name] === void 0);
      if (absent.length === 0) continue;
      const sample = sampleRow(
        { name: schema.name, persistence: schema.persistence, fields: absent, ...schema.identity ? { identity: true } : {} },
        { index, strategy: "seeded", persistence: schema.persistence }
      );
      for (const f of absent) {
        if (sample[f.name] !== void 0) {
          row[f.name] = sample[f.name];
        }
      }
    }
    mockLog.debug("mock:backfill", {
      storeKey,
      entity: schema.name,
      fields: candidates.map((f) => f.name)
    });
  }
  /**
   * Seed an entity with pre-authored instance data.
   */
  seedFromInstances(entityName, instances) {
    const store = this.getStore(entityName);
    if (this.config.debug) {
      mockLog.debug("seeding-from-instances", { count: instances.length, entity: entityName });
    }
    for (const instance of instances) {
      const id = instance.id || this.nextId(entityName);
      const item = {
        ...instance,
        id,
        createdAt: instance.createdAt || SEED_REFERENCE_TIMESTAMP,
        updatedAt: SEED_REFERENCE_TIMESTAMP
      };
      store.set(id, item);
    }
  }
  /**
   * Seed an entity with mock data.
   */
  seed(entityName, fields, count, persistence, identity) {
    const store = this.getStore(entityName);
    const normalized = entityName.toLowerCase();
    const storeKey = this.resolveStoreKey(entityName);
    if (this.config.debug) {
      mockLog.debug("seeding", { count, entity: entityName });
    }
    const ownerCols = this.parsedOwnerFields().filter(({ entity }) => entity.toLowerCase() === normalized).map(({ field }) => field).sort((a, b) => Number(b === "id") - Number(a === "id"));
    const ownerId = this.config.ownerId;
    const generated = [];
    let fallbackStamps = 0;
    for (let i = 0; i < count; i++) {
      const item = this.generateMockItem(entityName, fields, i + 1, persistence, identity);
      if (ownerId && ownerCols.length > 0) {
        for (const col of ownerCols) {
          if (!stampsRow(col, i)) continue;
          if (!this.viewerMayOwn(storeKey, col, item, ownerId)) {
            const fallbackId = this.firstEligibleOwner(storeKey, col, item);
            if (fallbackId === void 0) {
              this.ownerDeferredCells.push({ entity: storeKey, id: item.id, column: col });
              continue;
            }
            item[col] = fallbackId;
            this.ownerStampedCells.push({ entity: storeKey, id: item.id, column: col });
            fallbackStamps++;
            continue;
          }
          item[col] = ownerId;
          this.ownerStampedCells.push({ entity: storeKey, id: item.id, column: col });
        }
      }
      store.set(item.id, item);
      generated.push({
        id: item.id,
        updatedAt: typeof item.updatedAt === "string" ? item.updatedAt : ""
      });
    }
    mockLog.debug("mock:seed", () => ({
      entityName,
      count,
      idsAndTimestamps: JSON.stringify(generated),
      fallbackStamps
    }));
  }
  /** Parsed `config.ownerFields` pairs — every declared "Entity.field" owner
   *  column, split once so `seed()` and `linkRelationFields()` don't each
   *  re-parse the same strings. */
  parsedOwnerFields() {
    const out = [];
    for (const pair of this.config.ownerFields ?? []) {
      const [entity, field] = pair.split(".");
      if (entity && field) out.push({ entity, field });
    }
    return out;
  }
  /**
   * Generate a single mock item. Field values come from the canonical policy in
   * `@almadar/core/mock`; this method owns only the id and timestamp stamping.
   */
  generateMockItem(entityName, fields, index, persistence, identity) {
    const id = this.nextId(entityName);
    const createdAt = randomPastDate({ years: 1 });
    const updatedAt = new Date(
      Math.min(createdAt.getTime() + randomInt({ min: 0, max: 30 }) * MS_PER_DAY, Date.now())
    );
    return {
      ...sampleRow(
        { name: entityName, persistence, fields, ...identity ? { identity: true } : {} },
        { index, strategy: "seeded", persistence }
      ),
      id,
      createdAt: createdAt.toISOString(),
      updatedAt: updatedAt.toISOString()
    };
  }
  capitalizeFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  // ============================================================================
  // PersistenceAdapter Implementation
  // ============================================================================
  /** Synchronous twin of `create`, returning the stored row (the compiled path's `MockDataService` API). */
  insertRow(entityType, data) {
    const store = this.getStore(entityType);
    this.assertFileValuesWithinCeiling(entityType, data);
    const suppliedId = typeof data.id === "string" && data.id.length > 0 ? data.id : void 0;
    if (suppliedId && store.has(suppliedId)) {
      throw new Error(`Entity ${entityType} with id ${suppliedId} already exists`);
    }
    const id = suppliedId ?? this.nextId(entityType);
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const withDefaults = this.applyFieldDefaults(entityType, data);
    const item = {
      ...withDefaults,
      id,
      createdAt: isoTimestamp(data.createdAt) ?? now,
      updatedAt: isoTimestamp(data.updatedAt) ?? now
    };
    store.set(id, item);
    return item;
  }
  async create(entityType, data) {
    return { id: this.insertRow(entityType, data).id };
  }
  /**
   * Mock-mode file storage ceiling: a `file`-typed value whose `url` is a
   * `data:` URI keeps the whole payload in this in-memory/localStorage
   * store. Above the ceiling the write is REJECTED with a clear error —
   * never silently truncated or corrupted. Real deployments store bytes in
   * object storage and keep only the URL, so the ceiling is mock-only.
   */
  static FILE_DATA_URI_CEILING_BYTES = 2 * 1024 * 1024;
  assertFileValuesWithinCeiling(entityType, data) {
    for (const [fieldName, value] of Object.entries(data)) {
      if (value === null || typeof value !== "object" || Array.isArray(value) || value instanceof Date) continue;
      const candidate = value;
      if (typeof candidate.url !== "string" || !candidate.url.startsWith("data:")) continue;
      const bytes = typeof candidate.sizeBytes === "number" ? candidate.sizeBytes : candidate.url.length;
      if (bytes > _MockPersistenceAdapter.FILE_DATA_URI_CEILING_BYTES) {
        throw new Error(
          `${entityType}.${fieldName}: the playground mock store cannot persist files larger than ${_MockPersistenceAdapter.FILE_DATA_URI_CEILING_BYTES / (1024 * 1024)}MB (got ${(bytes / (1024 * 1024)).toFixed(1)}MB). Use a smaller file - production deployments store file bytes in object storage instead of the row.`
        );
      }
    }
  }
  /**
   * Fill in any entity-declared field defaults that the caller omitted.
   * SAVE payloads coming from form-section only carry the fields the user
   * edited; persisted rows should still honor `field.default` so downstream
   * row-content probes (VG11f) see a row whose every declared-default field
   * is non-empty. `@now` resolves to the current ISO timestamp.
   */
  applyFieldDefaults(entityType, data) {
    const schema = this.schemas.get(entityType.toLowerCase());
    if (!schema) return data;
    const result = { ...data };
    for (const field of schema.fields) {
      if (field.name !== void 0 && RESERVED_FIELD_NAMES.has(field.name)) continue;
      if (result[field.name] !== void 0) continue;
      if (field.default === void 0) continue;
      result[field.name] = field.default === "@now" ? (/* @__PURE__ */ new Date()).toISOString() : field.default;
    }
    return result;
  }
  /** Synchronous twin of `update`: the updated row, or null when there is no such row. */
  patchRow(entityType, id, data) {
    const store = this.getStore(entityType);
    this.assertFileValuesWithinCeiling(entityType, data);
    const existing = store.get(id);
    if (!existing) return null;
    const updated = {
      ...existing,
      ...data,
      id,
      // Preserve original ID
      updatedAt: isoTimestamp(data.updatedAt) ?? (/* @__PURE__ */ new Date()).toISOString()
    };
    store.set(id, updated);
    return updated;
  }
  async update(entityType, id, data) {
    if (!this.patchRow(entityType, id, data)) {
      throw new Error(`Entity ${entityType} with id ${id} not found`);
    }
  }
  /** Synchronous twin of `delete`: whether a row was removed. */
  removeRow(entityType, id) {
    return this.getStore(entityType).delete(id);
  }
  async delete(entityType, id) {
    if (!this.removeRow(entityType, id)) {
      throw new Error(`Entity ${entityType} with id ${id} not found`);
    }
  }
  rowOf(entityType, id) {
    return this.getStore(entityType).get(id) ?? null;
  }
  rowsOf(entityType) {
    return Array.from(this.getStore(entityType).values());
  }
  async getById(entityType, id) {
    return this.rowOf(entityType, id);
  }
  async list(entityType) {
    return this.rowsOf(entityType);
  }
  // ============================================================================
  // Utilities
  // ============================================================================
  /**
   * Clear all data for an entity.
   */
  clear(entityName) {
    const key = this.resolveStoreKey(entityName);
    this.stores.delete(key);
    this.idCounters.delete(key);
  }
  /** Clear all data + re-anchor the PRNG so the next seed loop reproduces
   *  identical rows. Hermetic-frame mode calls this between every step
   *  via OrbitalServerRuntime.resetMockPersistence. */
  clearAll() {
    this.stores.clear();
    this.idCounters.clear();
    this.ownerStampedCells = [];
    this.ownerDeferredCells = [];
    this.storeKeyByEntity.clear();
    this.idLabelByStoreKey.clear();
    this.storeNameById.clear();
    this.resetFakerSeed();
    mockLog.debug("mock:adapter:clearAll", { reanchored: this.config.seed });
  }
  /**
   * Get count of items for an entity.
   */
  count(entityName) {
    const store = this.getStore(entityName);
    return store.size;
  }
};
function createMockPersistence(config) {
  return new MockPersistenceAdapter(config);
}

export { InMemoryPersistence, MockPersistenceAdapter, createMockPersistence, entitySchemaOf };
//# sourceMappingURL=chunk-SHEPJ26Y.js.map
//# sourceMappingURL=chunk-SHEPJ26Y.js.map